import { useEffect, useState, useCallback } from "react";
import {
  getPinnedPostIts,
  updatePostIt,
  deletePostIt,
} from "../../api/postIts";
import useAuthStore from "../../store/authStore";
import PostIt from "../../pages/PostIts/PostItComponents/PostIt";
import "./PinnedPostIts.scss";

const PinnedPostIts = () => {
  const [pinnedPostIts, setPinnedPostIts] = useState([]);
  const token = useAuthStore((state) => state.token);

  const loadPinnedPostIts = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getPinnedPostIts(token);
      setPinnedPostIts(data);
    } catch (error) {
      console.error("Failed to load pinned post-its:", error);
    }
  }, [token]);

  // Load pinned post-its on mount or when userId changes
  useEffect(() => {
    loadPinnedPostIts();
  }, [loadPinnedPostIts]);

  // Set up event listeners (only once)
  useEffect(() => {
    // Listen for pin/unpin events from overlay
    const handlePinned = (e) => {
      const postIt = e.detail;
      setPinnedPostIts((prev) => {
        if (!prev.find((p) => p._id === postIt._id)) {
          return [...prev, postIt];
        }
        return prev;
      });
    };

    const handleUnpinned = (e) => {
      const postIt = e.detail;
      setPinnedPostIts((prev) => prev.filter((p) => p._id !== postIt._id));
    };

    window.addEventListener("postit-pinned", handlePinned);
    window.addEventListener("postit-unpinned", handleUnpinned);

    return () => {
      window.removeEventListener("postit-pinned", handlePinned);
      window.removeEventListener("postit-unpinned", handleUnpinned);
    };
  }, []); // Empty deps - only set up once

  const handleUpdatePostIt = async (id, updates) => {
    try {
      const updatedPostIt = await updatePostIt(id, updates, token);

      // If unpinning, remove from pinned list and notify overlay
      if (updates.isPinned === false) {
        setPinnedPostIts((prev) => prev.filter((p) => p._id !== id));
        // Notify overlay to add it back
        window.dispatchEvent(
          new CustomEvent("postit-unpinned", { detail: updatedPostIt })
        );
      }
      // Otherwise just update it in the pinned list
      else {
        setPinnedPostIts((prev) =>
          prev.map((p) => (p._id === id ? updatedPostIt : p))
        );
      }
    } catch (error) {
      console.error("Failed to update pinned post-it:", error);
    }
  };

  const handleDeletePostIt = async (id) => {
    try {
      await deletePostIt(id, token);
      setPinnedPostIts(pinnedPostIts.filter((p) => p._id !== id));
    } catch (error) {
      console.error("Failed to delete post-it:", error);
    }
  };

  const handleBringToFront = async (id) => {
    const maxZ = Math.max(...pinnedPostIts.map((p) => p.zIndex || 1), 1);
    const newZIndex = maxZ + 1;
    await handleUpdatePostIt(id, { zIndex: newZIndex });
  };

  if (pinnedPostIts.length === 0) {
    return null;
  }

  return (
    <div className="pinned-postits-container">
      {pinnedPostIts.map((postIt) => (
        <PostIt
          key={postIt._id}
          postIt={postIt}
          onUpdate={handleUpdatePostIt}
          onDelete={handleDeletePostIt}
          onBringToFront={handleBringToFront}
        />
      ))}
    </div>
  );
};

export default PinnedPostIts;
